// LivestreamOverlayFooter.js
'use strict';
import { connect } from 'react-redux';

import LivestreamOverlayFooter from '../component/LivestreamOverlayFooter.jsx';

import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getGoalData from '../selector/getGoalData.js';
import getGiftData from '../selector/getGiftData.js';
import getLiveStreamPrice from '../selector/getLiveStreamPrice.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getMeData from '../selector/getMeData.js';
import getMeBalance from '../selector/getMeBalance.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getPrivateShowProductId from '../selector/getPrivateShowProductId.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getShouldShowLivestreamCta from '../selector/getShouldShowLivestreamCta.js';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import sendLiveStreamGift from '../action/sendLiveStreamGift.js';
import fetchGift from '../action/fetchGift.js';
import login from '../action/login.js';
import addvalue from '../action/addvalue.js';
import unlockPrivateStream from '../action/unlockPrivateStream.js';
import pushToastr from '../action/pushToastr.js';

import {
  PRESET_SD,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import { Entry } from '../resource/loginRegisterConstants.js';
import {
  NEED_AUTH_HANDLER,
  LIVESTREAM_PAY_DURATION_SECONDS,
} from '../RemoteConfigKeys.js';

const mapStateToProps = (state, { streamId }) => {
  const fundingId = getLiveStreamData(
    state,
    [streamId, 'privateShow'],
    'fundingId'
  );
  const performingId = getLiveStreamData(
    state,
    [streamId, 'privateShow'],
    'performingId'
  );
  const giftId = getPrivateShowProductId(
    state,
    streamId,
    fundingId,
    performingId
  );
  const meId = getMeData(state, 'id');
  const isAuthed = !!meId;
  const balance = getMeBalance(state);
  const price = getLiveStreamPrice(state, streamId, PRESET_SD);
  const priceDurationMinutes =
    getRemoteConfigData(state, LIVESTREAM_PAY_DURATION_SECONDS) / 60;
  const hasBalance = getMeData(state, 'hasBalance');
  const privateLiveStreamStartTimestamp = getLiveStreamData(
    state,
    [streamId, 'presets', PRESET_SD],
    'nbf'
  );
  const isPrivateLiveStreamStarted = !!getLiveStreamData(
    state,
    [streamId],
    'isPrivateLiveStreamStarted'
  );
  const isPrivateStreamUnlocked = getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'isPrivateStreamUnlocked'
  );
  const hasStreamToken = !!getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'streamToken'
  );
  const isPrivateStreamSubscribed = getOperationData(
    state,
    ['pusher', 'channels'],
    `private-enc-stream@${streamId}`
  )?.subscribed;
  const productId = getLiveStreamData(state, [streamId, 'shop'], 'productId');
  const privateShowStatus = getPrivateShowStatus(state, streamId);
  const preset = getLiveStreamData(state, [streamId], 'preset');
  const streamTokenDecodedData = getStreamTokenDecodedData(state, streamId);
  const giftPrice = getGiftData(state, giftId, 'price');
  const performDuration = [
    privateShowStatusConstant.PERFORMING,
    privateShowStatusConstant.PREPARING,
  ].includes(privateShowStatus)
    ? getGoalData(state, [performingId, 'levels', 0], 'target')
    : getGoalData(state, [fundingId, 'context'], 'perform_duration');
  const isPresenceGoalSubscribed = getOperationData(
    state,
    ['pusher', 'channels', `presence-enc-goal@${fundingId}.${meId}`],
    'subscribed'
  );
  const isPresenceStreamViewerSubscribed = getOperationData(
    state,
    [
      'pusher',
      'channels',
      `presence-enc-stream-viewer@${streamId}.${preset}.${meId}`,
    ],
    'subscribed'
  );
  const isFetchingDrmLicense = getNetworkingData(
    state,
    ['drm', 'live', streamId],
    'isFetching'
  );
  const sessionId = getLiveStreamData(state, [streamId], 'sessionId');
  const isFetchingStreamToken = getNetworkingData(
    state,
    ['fetch', 'streamToken', sessionId],
    'isFetching'
  );
  const shouldShowPrice = isPrivateStreamSubscribed && price != null;
  const drmTestStatus = getOperationData(state, ['drm'], 'testStatus');
  const shouldShowLoadingOnUnlockButton =
    drmRulesetTestStatusConstant.TESTING === drmTestStatus ||
    !shouldShowPrice ||
    isFetchingDrmLicense ||
    isFetchingStreamToken ||
    (isPrivateLiveStreamStarted && isPrivateStreamUnlocked && !hasStreamToken);
  const playerStatus = getLiveStreamData(state, [streamId], 'playerStatus');
  const streamStatus = getLiveStreamData(state, [streamId], 'status');
  return {
    fundingId,
    performingId,
    giftId,
    privateShowStatus,
    playerStatus,
    drmTestStatus,
    performDuration,
    giftPrice,
    price,
    priceDurationMinutes,
    hasBalance,
    privateLiveStreamStartTimestamp,
    isPrivateLiveStreamStarted,
    productId,
    hasStreamToken,
    shouldShowPrice,
    shouldShowLoadingOnUnlockButton,
    shouldShowLivestreamCta: getShouldShowLivestreamCta(state, streamId),
    isPrivateStreamUnlocked,
    isPresenceGoalSubscribed,
    isPresenceStreamViewerSubscribed,
    isFetchingDrmLicense,
    isFetchingStreamToken,
    streamTokenDecodedData,
    streamStatus,
    isAuthed,
    balance,
    needAuthHandler: getRemoteConfigData(state, NEED_AUTH_HANDLER),
    giftSendingStatus: getOperationData(
      state,
      ['liveStream', 'gift'],
      'giftSendingStatus'
    ),
    isBalanceFetching: getNetworkingData(
      state,
      ['fetch', 'balance'],
      'isFetching'
    ),
    isSendGiftFetching: getNetworkingData(
      state,
      ['liveStream', streamId, 'gift', giftId],
      'isFetching'
    ),
    sendGiftFetchError: getNetworkingData(
      state,
      ['liveStream', streamId, 'gift', giftId],
      'error'
    ),
    isGiftFetched: getNetworkingData(
      state,
      ['gift', 'item', giftId],
      'isFetched'
    ),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    sendLiveStreamGift: data => dispatch(sendLiveStreamGift(data)),
    fetchGift: data => dispatch(fetchGift(data)),
    login: () => {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: { selectPath: ['login'], data: { entry: Entry.REGISTER } },
      });
      return dispatch(login());
    },
    addvalue: ({ via = 'gift' } = {}) => dispatch(addvalue({ via })),
    unlockPrivateStream: data => dispatch(unlockPrivateStream(data)),
    pushToastr: data => dispatch(pushToastr(data)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LivestreamOverlayFooter);
